import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/core';
import { AttendanceCorrection } from '../../types/attendance.types';
import {
  formatAttendanceDay,
  formatShiftEnd,
  formatTimeOfDay,
} from '../../services/attendance-rules';
import { CorrectionStatusTag } from '../correction-status-tag/correction-status-tag';

const CORRECTION_STATUSES = ['Pending', 'Approved', 'Rejected'];

interface CreateCorrectionRequestsTableColumnsProps {
  t: (key: string) => string;
}

export const createCorrectionRequestsTableColumns = ({
  t,
}: CreateCorrectionRequestsTableColumnsProps): ColumnDef<AttendanceCorrection>[] => [
  {
    id: 'CorrectionDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CORRECTION_DATE')} />,
    accessorFn: (row) => row.CorrectionDate,
    cell: ({ row }) => formatAttendanceDay(row.original.CorrectionDate),
  },
  {
    // Time of day on the organization's clock, without a day range: the page filters these two
    // columns itself (see `CorrectionRequestsTab`).
    id: 'StartTimeUtc',
    meta: { filter: { type: 'time' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_IN_TIME')} />,
    accessorFn: (row) => formatTimeOfDay(row.StartTimeUtc, false, row.Timezone),
  },
  {
    id: 'EndTimeUtc',
    meta: { filter: { type: 'time' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_OUT_TIME')} />,
    accessorFn: (row) => formatShiftEnd(row.StartTimeUtc, row.EndTimeUtc, false, row.Timezone),
  },
  {
    id: 'Comment',
    meta: { filter: { type: 'text' }, mobile: 'full' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('COMMENT')} />,
    // An empty or blank comment shows as '-' too, not just a missing one.
    accessorFn: (row) => row.Comment?.trim() || '-',
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: CORRECTION_STATUSES.map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => <CorrectionStatusTag status={row.original.Status} />,
  },
  {
    id: 'Fine',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('FINE')} />,
    // Charged on approval only: a pending request's amount is not a fine yet, a rejected one has none.
    accessorFn: (row) => (row.Status === 'Approved' && row.Fine ? row.Fine.toFixed(2) : '-'),
  },
];
